import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, define } from '#utils/styles'
import { localsBoolean as booleanControl } from '#components/form/form-boolean/lfds-boolean.css.ts'

const locals = scoped('navigation-list-row', [
	'background_color',
	'border_color',
	'color',
	'color_secondary'
] as const)

export default css`
	:host {
		${declare(locals, {
			background_color: tokens.color_background_secondary,
			border_color: tokens.color_border_primary,
			color: tokens.color_text_primary,
			color_secondary: tokens.color_text_secondary
		})};

		display: block;
		width: 100%;
		container-type: inline-size;
	}

	a.row {
		text-decoration: none;
	}

	.row {
		display: flex;
		align-items: center;
		box-sizing: border-box;
		width: 100%;
		gap: ${vars(tokens.spacing_lg)};
		position: relative;
		border: none;
		border-bottom: 1px solid ${vars(locals.border_color)};
		background: ${vars(locals.background_color)};
		padding-block: ${vars(tokens.spacing_md)};
		padding-inline: ${vars(tokens.spacing_lg)};
		text-align: start;
		font-family: ${vars(tokens.font_family_body)};
		font-size: ${vars(tokens.text_size_label_default)};
		font-weight: ${vars(tokens.font_weight_regular)};
		line-height: ${vars(tokens.line_height_label_default)};
		min-height: ${vars(tokens.size_control_lg)};
	}

	.body {
		width: 100%;
		flex-grow: 1;
	}

	slot[name='start'] {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	:host([data-has-start='false']) slot[name='start'] {
		display: none;
	}

	/*
    1. Icons in the end-slot should have the secondary icon color.
  */
	slot[name='end'] {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		transition: transform 125ms var(--ease-in);
		will-change: transform;
		--lfds--icon-color: ${vars(tokens.color_icon_secondary)}; /* [1] */
	}

	:host([data-has-end='false']) slot[name='end'] {
		display: none;
	}

	.interactive {
		position: relative;
		cursor: pointer;
		transition: 100ms var(--ease-in);
		transition-property: background-color;

		> * {
			z-index: 1;
		}

		&::after {
			content: '';
			position: absolute;
			z-index: 0;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: ${vars(tokens.color_state_layer_on_light)};
			opacity: 0;
			pointer-events: none;
		}

		&:focus-visible::after,
		&:hover::after {
			opacity: ${vars(tokens.effect_opacity_state_hover)};
			transition-timing-function: var(--ease-out);
		}

		&:focus {
			outline: 0;
		}

		&.focus-visible:focus-visible {
			outline: var(--focus-outline);
			outline-offset: calc(var(--focus-outline-offset) * -1);
		}
	}

	/* =========================================================================
	 * Selectable row (renders as <div>, click forwarded via JS)
	 * ========================================================================= */

	/*
	 * Propagate hover to the slotted checkbox border color.
	 * The checkbox state-layer background is intentionally not shown in
	 * a list-row context — the row itself provides the state layer.
	 */
	:host([selects]:hover) {
		${define(booleanControl, {
			border_color_control: vars(tokens.color_border_hover)
		})};
	}

	.selectable {
		position: relative;
		cursor: pointer;
		user-select: none;

		> * {
			z-index: 1;
		}

		&::after {
			content: '';
			position: absolute;
			z-index: 0;
			inset: 0;
			background-color: ${vars(tokens.color_state_layer_on_light)};
			opacity: 0;
			pointer-events: none;
			transition: 100ms opacity var(--ease-in);
		}

		/* Hover — row state layer */
		&:hover::after {
			opacity: ${vars(tokens.effect_opacity_state_hover)};
			transition-timing-function: var(--ease-out);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.interactive,
		.interactive::after,
		.selectable::after,
		slot[name='end'] {
			transition: none;
		}
	}
`
